'use client';

import React, { useEffect, useState } from 'react';

/**
 * ReadingProgress — fixed-top 3px teal bar that advances as the user scrolls.
 *
 * Spec: design-references/article.html `.progress`.
 *   - `position: fixed; top: 0; height: 3px`
 *   - teal fill, width transitions over 80ms
 *   - progress = scrollY / (documentHeight - viewportHeight), clamped [0,1]
 *
 * Scroll handler wrapped in `requestAnimationFrame` for frame-aligned updates.
 */

export function ReadingProgress() {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    let raf = 0;
    const compute = () => {
      const h = document.documentElement;
      const scrollable = h.scrollHeight - window.innerHeight;
      const scrolled = scrollable > 0 ? window.scrollY / scrollable : 0;
      setPct(Math.max(0, Math.min(1, scrolled)));
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div
      role="progressbar"
      aria-label="Reading progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct * 100)}
      className="fixed top-0 left-0 right-0 z-[100] h-[3px] bg-transparent"
    >
      <div
        className="h-full bg-aviado-teal transition-[width] duration-[80ms] ease-linear"
        style={{ width: `${pct * 100}%` }}
      />
    </div>
  );
}

export default ReadingProgress;
